import { useQuery } from '@tanstack/react-query';
import {
    Control,
    SubmitHandler,
    useFieldArray,
    UseFieldArrayReturn,
    useForm,
    UseFormGetValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormWatch,
} from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import {
    getAgentBehaviorListService,
    getAgentByIdServiceV2,
    getAgentPoliciesListService,
    getAllSkillsService,
    getReports,
    upsertAgentServiceV2,
    upsertAgentSkillServiceV2,
} from '../../../services/AgentService';
import {
    AgentBehavior,
    AgentSkills,
    AiAgentV2,
    AiAgentV2Data,
    ReportResponse,
    SkillResponse,
    SkillType,
} from '../../../types/AiAgentV2';
import { LoadingComponent } from '../../shared/LoadingComponent';
import AgentBehaviorComponent from './agentSetupComponent/AgentBehaviorComponent';
import AgentDeployComponent from './agentSetupComponent/AgentDeployComponent';
import AgentIdentityComponent from './agentSetupComponent/AgentIdentityComponent';
// import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { motion } from 'framer-motion';
import {
    ArrowLeft,
    ArrowRight,
    Brain,
    EllipsisVertical,
    GraduationCap,
    Palette,
    Save,
    SettingsIcon,
    User,
} from 'lucide-react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DeniedComponent } from 'src/App';
import { FileProps } from 'src/components/shared/DragAndDropV2';
import { Popover } from 'src/components/ui/popover';
import { findAllComponentService } from 'src/services/ComponentService';
import { ComponentType } from 'src/types/ComponentType';
import { AI_AGENT_V2_CREATE, AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { capitalize, grantPermission, validateAndTranslate } from 'src/utils';
import { PolicyIcon } from 'src/utils/iconsSVG';
import { AppAgentContext } from '../../../context/ContextProvider';
import { getModelServiceV2 } from '../../../services/ModelService';
import { getVectorStoreServiceV2 } from '../../../services/VectorStoreService';
import { appActions, DataStates } from '../../../store';
import { AgentSkillClass } from '../../../types/AgentSkillClass';
import { ModalType } from '../../../types/Modal';
import ButtonComponent from '../../shared/ButtonComponent';
import InteractiveTabsComponent, { InteractiveTab } from '../../shared/InteractiveTabsComponent';
import NavbarBreadcrumbV2 from '../../shared/NavbarBreadcrumbV2';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Button } from '../../ui/button';
import { AgentLayoutComponentV2 } from './AgentLayoutComponentV2';
import AgentComponent from './agentSetupComponent/AgentComponent';
import AgentPolicyComponent from './agentSetupComponent/AgentPolicyComponent';
import AgentSkillsComponent from './agentSetupComponent/agentSkillsComponent/AgentSkillsComponent';

export interface OldValuesAgent {
    main_prompt?: string;
    main_prompt_en?: string;
    greeting?: string;
    greeting_en?: string;
}

export type AgentForm = AiAgentV2Data & { new_skills?: AgentSkills[] };

interface FormContextType {
    watch: UseFormWatch<AgentForm>;
    setValue: UseFormSetValue<AgentForm>;
    getValues: UseFormGetValues<AgentForm>;
    control: Control<AgentForm>;
    skillsForm: UseFieldArrayReturn<AgentForm, 'skills', 'id'>;
    skillOptions: SkillResponse[];
    reportsOptions: ReportResponse[];
    modelOptions: { value: string; label: string }[];
    policiesOptions: AgentBehavior[];
    vectorStorageOptions: { value: string; label: string }[];
    behaviorOptions?: { value: string; label: string; data: string }[];
    skillFiles?: {
        [skill_id: number]: FileProps[];
    };
    getAgent: () => void;
    register: UseFormRegister<AgentForm>;
    setSkillFiles?: (files: (prevState: { [skill_id: number]: FileProps[] }) => any) => void;
    componentCmsList: { value: string; label: string; data: ComponentType }[];
}

const PROCESS_LENGTH = 2;

// Creamos el contexto con un valor inicial vacío (será sobrescrito luego)
export const AgentSetupContext = createContext<FormContextType>({
    watch: () => ({}) as any,
    setValue: () => {},
    getValues: () => ({}) as any,
    control: {} as Control<AgentForm>,
    modelOptions: [],
    vectorStorageOptions: [],
    behaviorOptions: [],
    policiesOptions: [],
    skillOptions: [],
    reportsOptions: [],
    getAgent: () => {},
    register: {} as UseFormRegister<AgentForm>,
    skillsForm: {} as UseFieldArrayReturn<AgentForm, 'skills', 'id'>,
    componentCmsList: [],
});

export const agentDefaultColor: { [key: string]: string } = {
    background_user_color: '#ffffff',
    text_agent_color: '#ffffff',
    text_user_color: '#000000',
    background_agent_color: '#0486e4',
    button_color: '#0486e4',
};

export const extraConfigDefault = {
    form_session: {
        welcome_title: 'Bienvenido a nuestro servicio de atención al cliente',
        phone: {
            title: 'Digita un número válido',
            placeholder: '300 123 4567',
            label: 'Número de Teléfono/WhatsApp',
        },
        name: {
            label: 'Nombre',
            placeholder: 'Ingresa tu nombre completo',
        },
        email: {
            label: 'Email',
            placeholder: 'correo@example.com',
        },
        privacy_policy_message: 'Acepto la política de tratamiento de datos',
        init_chat_message: 'Iniciar chat',
    },
    initial_message: 'Hola, ¿en qué puedo ayudarte hoy?',
    loading_suggestions_message: 'Cargando Sugerencias...',
    comunication_with_assesor_title: 'Comunicate con un asesor',
    minimized_chat_text: 'Minimizar',
    maximized_chat_text: 'Maximizar',
    finalized_chat_text: 'Finalizar chat',
    search_placeholder: 'Escribe tu pregunta aquí...',
    role_title: 'Hola :wave:, soy [AgentName]',
    role_message: 'Necesitas comunicarte con un asesor?',
    header_menu_color: '#ffffff',
    header_text_color: '#ffffff',
    logo_width: '25',
    loading_message_file: 'Loading...',
    loaded_message_file: 'Loaded!',
    call_agent_message: 'agent',
};

export default function AgentSetupComponent() {
    const navigate = useNavigate();
    const { id } = useParams();
    // const [loading, setLoading] = useState(State.IDLE);
    const [searchParams, setSearchParams] = useSearchParams();
    const [progressBar, setProgressBar] = useState<null | number>(null);
    // const [agentClass, setAgentClass] = useState(new AiAgentV2({}));
    const [skillFiles, setSkillFiles] = useState<{
        [skill_id: number]: FileProps[];
    }>({});
    // const [isAvailablePreview, setIsAvailablePreview] = useState(false);

    const { data, isLoading, refetch, isRefetching } = useQuery({
        queryKey: ['agent_detail', id],
        queryFn: async () => {
            if (!!id && id !== 'create') {
                return await getAgentByIdServiceV2(id as string);
            }
            return null;
        },
        // enabled: !!id && id !== 'create',
    });

    const {
        state: {
            session: {
                ai_agent_config: {
                    folders: { knowledge = '' },
                },
                user_id,
                permissions,
            },
        },
        dispatch,
    } = useContext(AppAgentContext);

    const [allowCreate, allowEdit] = useMemo(() => {
        return [
            grantPermission(AI_AGENT_V2_CREATE, permissions),
            grantPermission(AI_AGENT_V2_EDIT, permissions),
        ];
    }, [permissions]);

    const { data: modelOptions } = useQuery({
        queryKey: ['models'],
        queryFn: async () =>
            await getModelServiceV2()
                .then((res) => {
                    if (res.success) {
                        return (
                            res.items?.map((item) => ({
                                value: item,
                                label: capitalize(item),
                            })) ?? []
                        );
                    }

                    return [];
                })
                .catch((err) => err),
    });

    const { data: policiesOptions } = useQuery({
        queryKey: ['policies'],
        queryFn: async () =>
            await getAgentPoliciesListService()
                .then((res) => {
                    if (res.success) {
                        return res.items;
                    }
                    return [];
                })
                .catch((err) => err),
    });

    const { data: skillOptions } = useQuery({
        queryKey: ['skills'],
        queryFn: async () =>
            await getAllSkillsService()
                .then((res) => {
                    if (res.success) {
                        return res.items;
                    }
                    return [];
                })
                .catch((err) => err),
    });

    const { data: reportsOptions } = useQuery({
        queryKey: ['reports'],
        queryFn: async () =>
            await getReports()
                .then((res) => {
                    if (res.success) {
                        return res.items;
                    }
                    return [];
                })
                .catch((err) => err),
    });

    const { data: vectorStorageOptions } = useQuery({
        queryKey: ['vector_storage'],
        queryFn: async () =>
            await getVectorStoreServiceV2()
                .then((res) => {
                    if (res.success) {
                        return (
                            res.items?.map((item) => ({
                                value: item,
                                label: capitalize(item),
                            })) ?? []
                        );
                    }

                    return [];
                })
                .catch((err) => err),
    });

    const { data: behaviorOptions } = useQuery({
        queryKey: ['behavior_list'],
        queryFn: async () =>
            await getAgentBehaviorListService()
                .then((res) => {
                    if (res.success) {
                        return (
                            res.items?.map((item) => ({
                                value: item.name,
                                data: item.behavior,
                                label: capitalize(item.name),
                            })) ?? []
                        );
                    }

                    return [];
                })
                .catch((err) => err),
    });

    const { data: componentCmsList } = useQuery({
        queryKey: ['component_list'],
        queryFn: async () =>
            await findAllComponentService()
                .then((res) => {
                    if (res.success) {
                        return (
                            res.items?.map((item) => {
                                let id = '';
                                if (item.file_key) id = `source_file_${item.file_key}`;
                                else id = `source_model_${item._KEY}`;

                                return {
                                    value: id,
                                    data: item,
                                    label: capitalize(item.name),
                                };
                            }) ?? []
                        );
                    }
                    return [];
                })
                .catch((err) => err),
    });
    const [agentClass, setAgentClass] = useState(new AiAgentV2({}));

    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');
    const { t: customMessage } = useTranslate('custom-message');
    const [oldValues, setOldValues] = useState<OldValuesAgent>({});

    const { control, getValues, setValue, watch, handleSubmit, register } = useForm<AgentForm>({
        defaultValues: {
            name: '',
            context: '',
            behavior: '',
            domain: '',
            greeting: '',
            behavior_name: '',
            look_and_feel_chat: {
                suggested_message: [],
                avatar: '',
                agent_name: '',
                circular_alignment: null,
                privacy_policy_url: null,
                extra_config: extraConfigDefault,
                ...agentDefaultColor,
            },
            look_and_feel_voice: { agent_name: '' },
            look_and_feel_whatsapp: { agent_name: '' },
            privacy: {
                public: false,
                white_list_ips: [],
                white_list_countries: [],
                white_list_source: [],
                black_list_ips: [],
                black_list_countries: [],
                black_list_source: [],
                authentication_type: null,
                authentication_config: [],
                policies: [],
            },
            avatar: '',
            main_model: '',
            main_prompt: '',
            created_at: '',
            updated_at: '',
            skills: [],
            new_skills: [],
        },
    });

    useEffect(() => {
        const subscription = watch((form, { name }) => {
            if (
                form.look_and_feel_chat &&
                (name?.includes('look_and_feel_chat') || name === 'greeting' || name === 'skills')
            ) {
                setAgentClass(new AiAgentV2(form as AiAgentV2Data));
            }

            /* if (form.skills && id && id !== 'create') {
                 const fields = form.skills;
                 if (fields.length) {
                     if (
                         fields?.some(
                             (skill) =>
                                 skill?.skill_type &&
                                 [
                                     SkillType.SkillRetriever,
                                     SkillType.SkillUnstructuredRetriever,
                                     SkillType.SkillStructuredRetriever,
                                 ].includes(skill.skill_type as SkillType)
                         )
                     ) {
                         setIsAvailablePreview(
                             fields?.length === 1 ||
                                 (fields?.length > 1 && form.main_model!.length > 0 && form.main_prompt!.length > 0)
                         );
                     } else {
                         setIsAvailablePreview(false);
                     }
                 } else {
                     setIsAvailablePreview(false);
                 }
             }*/
        });
        return () => subscription.unsubscribe();
    }, [watch, id]);

    const skillsForm = useFieldArray({
        control,
        name: 'skills',
    });

    useEffect(() => {
        if (data?.item?.id) {
            if (data.item.look_and_feel_chat) {
                if (data.item.look_and_feel_chat?.extra_config) {
                    try {
                        data.item.look_and_feel_chat.extra_config = JSON.parse(
                            data.item.look_and_feel_chat.extra_config
                        );
                    } catch (e) {
                        console.error('Error parsing extra_config:', e);
                    }
                } else {
                    data.item.look_and_feel_chat.extra_config = extraConfigDefault;
                }
                setValue('look_and_feel_chat', {
                    ...data.item.look_and_feel_chat,
                    suggested_message: data.item.look_and_feel_chat?.suggested_message || [],
                });
            }

            if (data.item.privacy) {
                setValue('privacy', {
                    public: data.item.privacy?.public ?? false,
                    white_list_ips: data.item.privacy?.white_list_ips || [],
                    white_list_countries: data.item.privacy?.white_list_countries || [],
                    white_list_source: data.item.privacy?.white_list_source || [],
                    black_list_ips: data.item.privacy?.black_list_ips || [],
                    black_list_countries: data.item.privacy?.black_list_countries || [],
                    black_list_source: data.item.privacy?.black_list_source || [],
                    authentication_type: data.item.privacy?.authentication_type || null,
                    authentication_config: data.item.privacy?.authentication_config || [],
                    policies: data.item.privacy?.policies || [],
                });
            }

            if (data.item.skills) {
                const replaceSkill = data.item.skills.map((skill) => {
                    const data2 = { ...skill, skill_id: skill.id };
                    if ((skill.config.url || skill.config.folder) && skill.skill_type === SkillType.SkillRetriever) {
                        data2.skill_type = SkillType.SkillUnstructuredRetriever;
                    }
                    if (
                        (skill.config.model_name || skill.config.db_name || skill.config.file) &&
                        skill.skill_type === SkillType.SkillRetriever
                    ) {
                        data2.skill_type = SkillType.SkillStructuredRetriever;
                    }
                    return data2;
                });
                skillsForm.replace(replaceSkill);
            } else {
                skillsForm.replace([]);
            }

            if (data.item.look_and_feel_voice) {
                setValue('look_and_feel_voice', {
                    agent_name: data.item.look_and_feel_voice.agent_name || '',
                });
            }

            if (data.item.look_and_feel_whatsapp) {
                setValue('look_and_feel_whatsapp', {
                    agent_name: data.item.look_and_feel_whatsapp.agent_name || '',
                });
            }

            setValue('created_at', data.item.created_at ?? '');
            setValue('updated_at', data.item.updated_at ?? '');
            setValue('name', data.item.name ?? '');
            setValue('context', data.item.context ?? '');
            setValue('behavior', data.item.behavior ?? '');
            setValue('domain', data.item.domain ?? '');
            setValue('greeting', data.item.greeting ?? '');
            setValue('greeting_en', data.item.greeting_en ?? '');
            setValue('behavior_name', data.item.behavior_name ?? '');
            setValue('avatar', data.item.avatar ?? '');
            setValue('main_model', data.item.main_model ?? '');
            setValue('main_prompt', data.item.main_prompt ?? '');
            setValue('main_prompt_en', data.item.main_prompt_en ?? '');

            setOldValues({
                main_prompt: data.item.main_prompt ?? '',
                main_prompt_en: data.item.main_prompt_en ?? '',
                greeting: data.item.greeting ?? '',
                greeting_en: data.item.greeting_en ?? '',
            });
        }
    }, [data]);

    const percentage =
        progressBar !== null ? (progressBar !== 0 ? Math.floor((progressBar / PROCESS_LENGTH) * 100) : 0) : null;

    const onSubmit: SubmitHandler<AgentForm> = async (data) => {
        const submittedData = {
            ...data,
        };

        if (!data.name || !data.main_model || !data.main_prompt) {
            searchParams.set('mode', 'agent');
            setSearchParams(searchParams);
            toast(
                <div className="flex flex-col">
                    <b>{customMessage('should_complete_following_fields')}:</b>
                    <ul className="list-disc px-3">
                        {!data.name && <li>{common('name')}</li>}
                        {!data.main_model && <li>{common('model')}</li>}
                        {!data.main_prompt && <li>{agent('prompt')}</li>}
                    </ul>
                </div>,
                { richColors: true }
            );
            return;
        }

        dispatch(
            appActions.openModal({
                type: ModalType.CONFIRMATION,
                data: {
                    title: `${common(id !== 'create' ? 'update' : 'save')} ${agent('agent')}`,
                    titleClassName: 'text-2xl font-bold text-purple-800',
                    description: `${customMessage(id !== 'create' ? 'warn-update' : 'warn-save')} ${common(id !== 'create' ? 'update' : 'save')} ${agent('agent')}?`,
                    className:
                        '!bg-gradient-to-br !from-purple-50 !to-pink-50 !rounded-3xl !shadow-2xl w-full max-w-md overflow-hidden',
                    cancelButtonClassName: 'border-pink-500 btn text-pink-600 hover:bg-pink-50 w-[200px]',
                    confirmButtonClassName:
                        'bg-gradient-to-r btn from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white w-[200px]',
                    state: DataStates.AGENT,
                    async onConfirm() {
                        const newSkills = data.new_skills ? [...data.new_skills] : [];

                        if (id && id !== 'create') {
                            submittedData.id = parseInt(id);
                        }
                        dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                        setProgressBar(0);

                        const fields: { key: keyof AgentForm; keyEn: keyof AgentForm }[] = [
                            { key: 'greeting', keyEn: 'greeting_en' },
                            { key: 'main_prompt', keyEn: 'main_prompt_en' },
                        ];

                        await validateAndTranslate<AgentForm, OldValuesAgent>(
                            data,
                            oldValues,
                            submittedData,
                            fields,
                            setProgressBar,
                            id === 'create'
                        );
                        submittedData.look_and_feel_chat.extra_config = JSON.stringify(
                            data.look_and_feel_chat.extra_config || extraConfigDefault
                        );
                        const response = await upsertAgentServiceV2(submittedData);

                        if (response?.success) {
                            setProgressBar(2);
                            const agent_id = response.item?.id;
                            if (newSkills.length > 0 && id === 'create') {
                                for await (const skill of [...newSkills]) {
                                    if (agent_id) {
                                        // First create skill
                                        const responseSkill = await upsertAgentSkillServiceV2({ ...skill }, agent_id);
                                        if (!response.success) {
                                            toast.error(`${agent('agent_created_skill_error')} ${skill.name}`);
                                        } else {
                                            const skill_id = (responseSkill.item as { skill_id: number })?.skill_id;
                                            const nSkill = new AgentSkillClass(skill, knowledge, {});
                                            nSkill.skill_id = skill_id;
                                            const response = await nSkill.uploadFolders(parseInt(id), user_id);
                                            if (!response?.success) {
                                                toast.error(
                                                    `${agent('agent_created_skill_error')} ${nSkill.getSkill().name}`
                                                );
                                            }
                                        }
                                    }
                                }
                            }
                            // reset();

                            if (!id || id === 'create') {
                                endProcessForm();
                                setProgressBar(null);
                                navigate(`/agent_setup/${response.item.id}`);
                            } else {
                                endProcessForm();
                                setProgressBar(null);
                                refetch();
                            }
                        } else {
                            toast.error(agent(id ? 'agent_updated_error' : 'agent_created_error'));
                        }
                    },
                    onClose() {
                        dispatch(appActions.closeModal(ModalType.CONFIRMATION));
                    },
                },
            })
        );
    };

    const endProcessForm = () => {
        toast.success(agent(id ? 'agent_updated_successfully' : 'agent_created_successfully'));
        setValue('new_skills', []);
    };

    const tabs: InteractiveTab[] = useMemo(
        () => [
            { id: 'agent', label: agent('agent'), color: '#E11D48', icon: <User />, component: <AgentComponent /> },
            {
                id: 'identity',
                label: agent('identity'),
                color: '#FF9F1C',
                icon: <Palette />,
                component: <AgentIdentityComponent />,
            },
            {
                id: 'policy',
                label: agent('policy'),
                color: '#47a93d',
                icon: <PolicyIcon width={35} height={35} fill="white" />,
                component: <AgentPolicyComponent />,
            },
            {
                id: 'behavior',
                label: agent('behavior'),
                color: '#2EC4B6',
                icon: <Brain />,
                component: <AgentBehaviorComponent />,
            },
            {
                id: 'skills',
                label: agent('skills'),
                color: '#9B5DE5',
                icon: <GraduationCap />,
                component: <AgentSkillsComponent />,
            },
            {
                id: 'deploy',
                label: agent('deploy'),
                color: '#00B4D8',
                icon: <SettingsIcon />,
                component: <AgentDeployComponent />,
            },
        ],
        []
    );

    if (isLoading || isRefetching) {
        return <LoadingComponent />;
    }

    if (id === 'create' && !allowCreate) {
        return <DeniedComponent />;
    }

    return (
        <div
            className={`grid grid-cols-1 xl:grid-cols-[2fr_auto] min-h-screen bg-gradient-to-br from-pink-100 to-indigo-100 p-8`}
        >
            <div className="container mx-auto p-4 space-y-4">
                <NavbarBreadcrumbV2
                    routes={[
                        {
                            label: agent('agent_list'),
                            route: '/agent_list',
                        },
                        { label: `${id === 'create' ? agent('create_new_agent') : `${agent('agent')} ${id}`}` },
                    ]}
                />

                <AgentSetupContext.Provider
                    value={{
                        getValues,
                        control,
                        setValue,
                        watch,
                        skillsForm,
                        modelOptions,
                        vectorStorageOptions,
                        policiesOptions: policiesOptions || [],
                        skillOptions,
                        reportsOptions,
                        behaviorOptions,
                        skillFiles,
                        register,
                        getAgent: refetch,
                        setSkillFiles,
                        componentCmsList,
                    }}
                >
                    <div className="flex flex-col gap-2 bg-white rounded-lg shadow-lg p-6">
                        <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-3">
                            <div className="flex items-center gap-2">
                                <Button
                                    onClick={() => {
                                        navigate('/agent_list');
                                    }}
                                    className="bg-transparent hover:bg-purple-100 text-purple-600 p-2 rounded-full transition-colors duration-300"
                                >
                                    <ArrowLeft size={24} />
                                </Button>
                                <h1 className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 flex items-center gap-2">
                                    {data?.item?.avatar && (
                                        <Avatar>
                                            <AvatarImage src={data?.item.avatar} alt={data?.item.name} />
                                            <AvatarFallback>{data?.item.name[0]}</AvatarFallback>
                                        </Avatar>
                                    )}
                                    {data?.item?.name ?? agent('new_agent')}
                                </h1>
                            </div>
                            <div className="flex items-center gap-2">
                                {((id === 'create' && allowCreate) || (id !== 'create' && allowEdit)) && (
                                    <ButtonComponent
                                        icon={<Save className="w-[16px] h-[16px]" />}
                                        loading={progressBar !== null}
                                        className="flex items-center gap-1 w-full sm:w-auto bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-semibold py-2 px-6 rounded-full transition-all duration-300 transform hover:scale-105"
                                        label={`${common(id !== 'create' ? 'update' : 'save')} ${agent('agent')}`}
                                        disabled={progressBar !== null}
                                        onClick={() => {
                                            handleSubmit(onSubmit)();
                                        }}
                                    />
                                )}
                                {data?.item.id && (
                                    <Popover>
                                        <PopoverTrigger>
                                            <EllipsisVertical className="w-[20px] h-[20px]" />
                                        </PopoverTrigger>
                                        <PopoverContent className="bg-white shadow-xl rounded-lg w-[180px] mr-6 p-1 xl:mr-0 z-[50]">
                                            <div className="flex flex-col gap-1">
                                                <ButtonComponent
                                                    icon={<ArrowRight className="w-[16px] h-[16px]" />}
                                                    className="w-full flex text-black  bg-white items-center justify-start gap-2 px-2 h-8 text-sm rounded-sm hover:bg-gray-100 transition-colors text-left"
                                                    label={`Go to markdown`}
                                                    onClick={() => {
                                                        navigate(`/mark-down/chat/${data?.item.id}`);
                                                    }}
                                                />
                                                <ButtonComponent
                                                    icon={<ArrowRight className="w-[16px] h-[16px]" />}
                                                    className="w-full flex text-black bg-white items-center justify-start gap-2 px-2 h-8 text-sm rounded-sm hover:bg-gray-100 transition-colors text-left"
                                                    label={`Go to preview script`}
                                                    onClick={() => {
                                                        window.open(
                                                            `/ai-studio/#/bot-script/${data?.item.id}`,
                                                            '_blank'
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                )}
                            </div>
                        </div>

                        <InteractiveTabsComponent
                            tabs={tabs.filter((tab) => !(id === 'create' && tab.id === 'deploy'))}
                        />
                    </div>
                </AgentSetupContext.Provider>
            </div>

            <div className="mt-36 xl:flex hidden">
                <AgentLayoutComponentV2
                    id="agent_layout_v2"
                    agentId={id !== 'create' ? id : undefined}
                    messages={[]}
                    agent={agentClass}
                    isAvailablePreview={!(!!watch('main_model') && watch('main_model').length > 0)}
                    testSkill
                />
            </div>
            {percentage !== null && (
                <div className="w-screen z-[30] flex justify-center items-center h-dvh bg-black-transparent fixed top-0 left-0">
                    <div className="p-10 min-w-[700px] bg-white shadow  rounded">
                        <motion.div
                            className="h-4 rounded  bg-gradient-to-r from-purple-600 to-pink-600 mb-6"
                            initial={{ width: 0 }}
                            animate={{ width: `${percentage}%` }}
                            transition={{ duration: 0.5, ease: 'easeInOut' }}
                        />
                        <div className="flex justify-between items-center">
                            <div className="text-lg font-bold">{agent('agent_saving_data')}</div>
                            <div className="text-lg font-bold">{percentage}%</div>
                            <div className="text-lg font-bold">
                                {progressBar}/{PROCESS_LENGTH}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
